import React from 'react';
import { Box } from '@mui/material';

export type HighlightMatchProps = {
  text: string;
  query: string;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Same rules as Spotlight normalize — keep HighlightMatch free of layout imports. */
export const normalizeForHighlight = (value: string): string =>
  value
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();

const markSx = {
  bgcolor: 'warning.light',
  color: 'inherit',
  fontWeight: 700,
  px: 0.15,
  borderRadius: 0.5,
} as const;

const isAcronymQuery = (normalized: string): boolean => {
  if (normalized.length < 2) return false;
  if (/\s/.test(normalized)) return false;
  return /^[a-z0-9]+$/.test(normalized);
};

export type AcronymHighlightPlan = {
  /** Indices into whitespace-split segments that should mark their first char. */
  segmentIndexes: number[];
};

/**
 * Returns which word segments to initial-highlight, or null when query is not
 * an acronym match for `text`.
 * Tries the full phrase first, then suffixes (e.g. `lh` on "Tạo lịch hẹn"
 * highlights lịch + hẹn — matches keyword-style hits while the label starts with Tạo).
 */
export const planAcronymHighlight = (
  text: string,
  query: string,
): AcronymHighlightPlan | null => {
  const q = normalizeForHighlight(query);
  if (!isAcronymQuery(q)) return null;

  const segments = text.split(/(\s+)/);
  const wordIndexes: number[] = [];
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i]!.trim().length > 0) wordIndexes.push(i);
  }

  for (let start = 0; start < wordIndexes.length; start += 1) {
    const slice = wordIndexes.slice(start);
    const acronym = slice
      .map((idx) => normalizeForHighlight(segments[idx]![0] ?? ''))
      .join('');
    if (acronym.length < 2) continue;
    if (acronym === q || acronym.startsWith(q)) {
      return { segmentIndexes: slice.slice(0, q.length) };
    }
  }

  return null;
};

/**
 * Highlights query tokens (whitespace-split, case-insensitive) inside `text`.
 * Contiguous letter queries that match the label acronym also highlight initials
 * (e.g. `tlh` → **T**ạo **l**ịch **h**ẹn).
 */
const HighlightMatch: React.FC<HighlightMatchProps> = ({ text, query }) => {
  const trimmed = query.trim();
  if (!text || !trimmed) return <>{text}</>;

  const plan = planAcronymHighlight(text, trimmed);
  if (plan) {
    const segments = text.split(/(\s+)/);
    const highlightWord = new Set(plan.segmentIndexes);
    return (
      <>
        {segments.map((segment, index) => {
          if (!highlightWord.has(index) || segment.length === 0) {
            return (
              <React.Fragment key={`s-${index}`}>{segment}</React.Fragment>
            );
          }
          const first = segment[0]!;
          const rest = segment.slice(1);
          return (
            <React.Fragment key={`s-${index}`}>
              <Box component="mark" sx={markSx}>
                {first}
              </Box>
              {rest}
            </React.Fragment>
          );
        })}
      </>
    );
  }

  const tokens = trimmed
    .split(/\s+/)
    .filter((token) => token.length > 0)
    .sort((a, b) => b.length - a.length);

  if (tokens.length === 0) return <>{text}</>;

  const pattern = tokens.map(escapeRegExp).join('|');
  const parts = text.split(new RegExp(`(${pattern})`, 'gi'));
  const tokenSet = new Set(tokens.map((token) => token.toLowerCase()));

  return (
    <>
      {parts.map((part, index) =>
        tokenSet.has(part.toLowerCase()) ? (
          <Box component="mark" key={`${part}-${index}`} sx={markSx}>
            {part}
          </Box>
        ) : (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

export default HighlightMatch;
